import { useEffect, useRef, useState } from 'react'
import Layer from '../../utils/Layers'
import '../../utils/L.RealtimeCanvasLayer.js'
import Terrier from '../../utils/terrier'
import { useMap, useMapEvents } from 'react-leaflet'
import MediaControls from './Media Controls/MediaControls'
import Legend from './Legend/Legend'
import { useDispatch, useSelector } from 'react-redux'
import { setCurLayer } from './WetDogWeatherProductsSlice'
import { WDWSTACK } from '../../environment/apis.config'
import L, {
  LeafletMouseEvent,
} from 'leaflet'
import './WetDogWeatherLayers.css'
import { WetDogWeatherLayersEnum } from '../../interfaces/WetDogWeatherLayers'
import {
  convertValuesToTruweatherUnits,
  formatValue,
  LayerToUnitsMapping,
} from '../../utils/WetDogWeatherUtils'
import useTruWeatherUnits from '../../hooks/useCustomThresholds'

// Generous padding applied to a region's geobounds before testing viewport
// overlap (see recomputeAvailableTimes/regionGeoInfo below). A region's
// declared geobounds can be drawn a bit tight/offset relative to where its
// tiles actually render (observed ~7km short on one region's edge), which
// previously caused a real region to be wrongly excluded just from zooming
// in on it. ~2 degrees (~200km) comfortably absorbs that kind of drawing
// imprecision while still being negligible next to the intercontinental
// distances this filter actually needs to distinguish (e.g. Nigeria vs a
// CONUS-only model).
const GEO_FILTER_PAD_DEGREES = 2

// Bounding-box overlap check mirroring Terrier's own region matching
// (see TerrierInsideOrOnEdgeOneWay/TerrierOverlapOneWay in terrier.js).
// Boxes are [west, south, east, north], same order as `region.geobounds`.
function boundsOverlap(a: number[], b: number[]) {
  return a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3]
}

// A layer's `sources` cover every region that source supports (e.g. a
// CONUS-only high-res model plus a global fallback), all loaded up front
// regardless of where the map is pointed - `manifest.region` alone doesn't
// tell us whether that region is actually relevant to what's on screen.
// This looks up the geobounds/isglobal flag Terrier already has for a named
// region so we can gate ticks by whether they'd actually apply to the
// current view (e.g. excluding a CONUS model's ticks while looking at
// Nigeria, but always keeping a global model's).
function regionGeoInfo(regionName: string): { isGlobal: boolean; geobounds: number[] | null } | null {
  const sources = (Terrier as any).stackContents?.sources ?? []
  for (const source of sources) {
    const region = (source.regions ?? []).find((r: any) => r.name === regionName)
    if (region) {
      return { isGlobal: !!region.isglobal, geobounds: region.geobounds ?? null }
    }
  }
  return null
}

const WetDogWeatherLayers = () => {
  const [legendVisible, setLegendVisible] = useState(true)
  const [layers, setLayers] = useState<Layer[]>([])
  const [level, setLevel] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [animSpeed, setAnimSpeed] = useState(5.0)
  const [timeRange, setTimeRange] = useState([0.0, 0.0])
  const [curTime, setCurTime] = useState(Number.NEGATIVE_INFINITY)
  const [displayedTime, setDisplayedTime] = useState(Number.NEGATIVE_INFINITY)
  const [terrierOvl, setTerrierOvl] = useState(null)
  const [units, _setUnits] = useState('')
  const [canvasLayer] = useState(L.realtimeCanvasLayer())
  const [radarOverlay, setRadarOverlay] = useState(null)
  // Real tile timestamps pulled from the data manifests for the current layer
  // (populated by onManifestLoad below). This is what MediaControls uses to
  // build its tick marks and to snap the scrubber, since it reflects whatever
  // mix of model cadences actually produced the tiles.
  const [availableTimes, setAvailableTimes] = useState<number[]>([])
  // A layer's `sources` can bundle multiple model combos that report the
  // SAME geographic `region` (e.g. wind_uv mixes hourly 'gfs'/'hrrr' with
  // 20-min high-res 'mmatic'/'cuwx', and several of those share region
  // 'conus') - Boxer's `region` is purely geographic, not a source/model
  // identifier, so onManifestLoad fires once per SOURCE but multiple
  // sources collide on one region key. Each region's slot therefore has to
  // accumulate (union) epochs across every manifest arrival for that key,
  // not replace them, or a coarser source's manifest landing after a finer
  // one wipes out the finer source's ticks (and vice versa) - which is what
  // caused specific high-res timestamps to go missing from the slider.
  const regionTimesRef = useRef<Record<string, number[]>>({})

  const canvasRef = useRef(null)
  const map = useMap()
  const curLayer = useSelector((state) => state.WetDogWeatherProducts.curLayer)
  const dispatch = useDispatch()
  const { data: customUnits, isPending, isError } = useTruWeatherUnits();
  // Mirrors `terrierOvl` state but updates synchronously (no re-render lag),
  // so recomputeAvailableTimes can call it from a moveend handler right after
  // startup without racing the setTerrierOvl(ovl) state update below.
  const terrierOvlRef = useRef<any>(null)

  // Re-derive availableTimes/timeRange from everything loaded so far.
  // `applyViewportFilter` gates the geographic relevance check (global
  // regions always count; regional ones only count if their padded
  // geobounds overlaps the viewport):
  // - On manifest arrival, the map can still be mid-layout/resize during the
  //   initial load burst, so `map.getBounds()` is not trustworthy yet -
  //   manifest-triggered recomputes always union in everything.
  // - Only the `moveend`-triggered recompute applies the filter, since by
  //   then the map is guaranteed settled and the user has actually
  //   navigated (e.g. hiding a CONUS model's ticks once panned to Nigeria).
  //   The bbox padding (GEO_FILTER_PAD_DEGREES) exists because a region's
  //   declared geobounds can be drawn a bit tight/offset from where its
  //   tiles actually render - without it, this filter previously excluded a
  //   real, currently-relevant region just from zooming in on it.
  const recomputeAvailableTimes = (applyViewportFilter: boolean) => {
    const ovl = terrierOvlRef.current
    if (!ovl) return
    const regionEntries = Object.entries(regionTimesRef.current)
    if (regionEntries.length === 0) return

    const bounds = map.getBounds()
    const mapBounds = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ]

    const relevantEpochs: number[] = []
    regionEntries.forEach(([region, epochs]) => {
      const info = regionGeoInfo(region)
      const paddedBounds = info?.geobounds
        ? [
            info.geobounds[0] - GEO_FILTER_PAD_DEGREES,
            info.geobounds[1] - GEO_FILTER_PAD_DEGREES,
            info.geobounds[2] + GEO_FILTER_PAD_DEGREES,
            info.geobounds[3] + GEO_FILTER_PAD_DEGREES,
          ]
        : null
      // Regions we can't classify fall through as relevant - better to show
      // an extra tick than silently hide real data we don't understand.
      const included =
        !applyViewportFilter ||
        !info ||
        info.isGlobal ||
        !!(paddedBounds && boundsOverlap(paddedBounds, mapBounds))
      if (included) {
        relevantEpochs.push(...epochs)
      }
    })
    if (relevantEpochs.length === 0) return

    const merged = Array.from(new Set(relevantEpochs)).sort((a, b) => a - b)
    setAvailableTimes(merged)

    const newTimeRange = [merged[0], merged[merged.length - 1]]
    ovl.setTimeRange(newTimeRange[0] * 1000, newTimeRange[1] * 1000)
    setTimeRange(newTimeRange)
    // Keep whatever position is already displayed/playing - only clamp into
    // the new range if it's now out of bounds. Forcing this to the newest
    // tick on every manifest arrival (and every map pan, since this also
    // runs on moveend) silently moved Terrier's internal playhead to the end
    // behind React's curTime state, so pressing Play resumed from "the end"
    // instead of from wherever the scrubber actually showed.
    if (curTime < newTimeRange[0]) {
      setCurTime(newTimeRange[0])
    } else if (curTime > newTimeRange[1]) {
      setCurTime(newTimeRange[1])
    } else {
      ovl.setCurrentTime(curTime)
    }
  }

  // Sources/manifests are fetched once at startup and never re-fetched on
  // pan, so the tick list has to be re-filtered against the new viewport
  // whenever the user moves the map.
  useMapEvents({
    moveend: () => recomputeAvailableTimes(true),
  })

  const startUpFunc = () => {
    if (map !== null) {
      //terrier now requires an api key param.
      //our stack doesn't reqiure auth yet. Hence providing a random string
      //again this is NOT A SECURITY ISSUE!!
      Terrier.startLeaflet(WDWSTACK, '7b372fcb-249a-4d45-8c5b-8e85acbb84fb', canvasLayer, (ovl) => {
        terrierReady(ovl)
      })

      canvasLayer.addTo(map)
    }
  }

  useEffect(() => {
    for (let layerId = 0; layerId < layers.length; layerId++) {
      if (layerId != curLayer.layer) {
        layers[layerId].enable(false)
      }
      map.closePopup()
    }
    const now = new Date().getTime() / 1000
    // Clear out the previous layer's manifest times (and per-region
    // accumulator) so the newly-enabled layer's own manifests start from a
    // clean slate instead of merging with the old layer's leftover ticks.
    setAvailableTimes([])
    regionTimesRef.current = {}
    // Then turn ours on
    if (curLayer.layer >= 0 && curLayer.layer < layers.length) {
      const layer = layers[curLayer.layer]
      layer.enable(true)

      //update the time range
      let timeRange = layer.layer.ovl.getTimeRange()
      if (timeRange[0] != timeRange[1]) {
        timeRange[0] = timeRange[0] / 1000 - now
        timeRange[1] = timeRange[1] / 1000 - now
      } else {
        timeRange = layer.timeRange
      }
      setTimeRange([now + timeRange[0], now + timeRange[1]])
      if (curTime < timeRange[0] + now) {
        setCurTime(timeRange[0] + now)
      } else if (curTime >= timeRange[1] + now) {
        setCurTime(timeRange[1] + now)
      }

      // And update the units of whatever is being displayed
      _setUnits(layer.units)
      layers[curLayer.layer].getDisplayName() === 'Wind80m'
        ? layers[curLayer.layer].setLevel('80m')
        : layers[curLayer.layer].setLevel(null)
      map.getContainer().classList.remove('leaflet-grab')
      map.getContainer().classList.add('pointer-cursor')
    } else {
      setCurTime(now)
      setTimeRange([0.0, 0.0])
      _setUnits('')
      setLevel(null)
      map.getContainer().classList.add('leaflet-grab')
      map.getContainer().classList.remove('pointer-cursor')
      if (radarOverlay) {
        map.removeLayer(radarOverlay)
      }
    }
  }, [curLayer.layer])

  // React to curTime changes
  useEffect(() => {
    if (curTime == Number.NEGATIVE_INFINITY) {
      return
    }
    if (terrierOvl == undefined) {
      return
    }
    terrierOvl.setCurrentTime(curTime)
    setDisplayedTime(terrierOvl.getCurrentTime)
  }, [curTime])

  // React to isPlaying changes
  useEffect(() => {
    if (terrierOvl == undefined) {
      return
    }
    if (isPlaying) {
      // Re-assert the engine's playhead from curTime before resuming -
      // anything that nudges Terrier's internal time without going through
      // setCurTime (e.g. recomputeAvailableTimes) would otherwise make
      // timePlay resume from wherever that left it instead of from curTime.
      terrierOvl.setCurrentTime(curTime)
      terrierOvl.timePlay({ period: 30.0 / animSpeed, pause: 2.0 })
    } else {
      terrierOvl.timePause()
      // We were animating, so update our curTime from Terrier
      setCurTime(terrierOvl.getCurrentTime())
    }
  }, [isPlaying, animSpeed])

  // Add an interval callback to update the curTime periodically when isPlaying is on
  // TODO: Turn this off when we don't need it
  const updatePlayTime = () => {
    if (terrierOvl == undefined) {
      return
    }
    if (!isPlaying) {
      return
    }
    const newTime = terrierOvl.getCurrentTime()
    // if (curTime != newTime) {
    //   // TODO: Check that we're not creating a slow recursion here
    //   setCurTime(newTime)
    // }
    //the update to setCurTime causes flickering of the layer. Instead update setDisplayedTime directly.
    setDisplayedTime(newTime)
  }
  useEffect(() => {
    const interval = setInterval(() => updatePlayTime(), 100)
    return () => {
      clearInterval(interval)
    }
  }, [terrierOvl, isPlaying, curTime])

  function timeRangeForVariable(variable) {
    switch (variable.dataType) {
      case 'temperature':
      case 'wind_uv':
        return [-1 * 24 * 60 * 60, 1 * 24 * 60 * 60, 300]
      case 'velocity':
      case 'visibility':
      case 'cloudceiling':
        return [-1 * 24 * 60 * 60, 1 * 24 * 60 * 60, 300]
      case 'visual':
        return variable.source === 'xweather'
          ? [-86400, 86400, 12]
          : [0, 86400, 32]
    }
    switch (variable.temporalType) {
      // Just observed data
      case 'observed':
        return [-4 * 60 * 60, 0, 300]
      // Forecast and both we'll do the same
      case 'forecast':
      case 'both':
        return [-1 * 24 * 60 * 60, 1 * 24 * 60 * 60, 300]
    }

    return [-1 * 24 * 60 * 60, 1 * 24 * 60 * 60, 300]
  }

  function interpForVariable(variable) {
    switch (variable.dataType.toLowerCase()) {
      case 'severehailindex':
        return 0
      case 'preciptype':
        return 0
      case 'none':
        if (variable.name.includes('swath')) {
          return 0
        } else if (variable.name.includes('size')) {
          return 0
        } else if (variable.name.includes('hail')) {
          return 0
        }
        if (variable.name.includes('qpe_ffg')) {
          return 0
        }
    }

    return 1
  }

  function unitsForVariable(variable): string {
    switch (variable.name) {
      case 'cloud_ceiling':
        return customUnits['ceiling' as keyof typeof customUnits]
      case 'temperature':
        return customUnits['temperature' as keyof typeof customUnits]
      case 'visibility':
        return customUnits['visibility' as keyof typeof customUnits] || 'sm'
      case 'wind_uv':
      case 'wind_speed_gust':
        return customUnits['wind_speed' as keyof typeof customUnits]
      default:
        return variable.units
    }
  }

  const terrierReady = (ovl) => {
    setTerrierOvl(ovl)
    terrierOvlRef.current = ovl

    // Shared by every layer's `loadCallback` below. Each source in a layer's
    // `sources` array delivers its own manifest independently (one call per
    // source, though several sources can report the same `region`), so this
    // unions the newly-arrived epochs into that region's slot, then
    // re-derives the displayed range/ticks/current-time via
    // recomputeAvailableTimes - unfiltered, since the map viewport isn't
    // trustworthy yet during this initial load burst (see
    // recomputeAvailableTimes for why).
    const onManifestLoad = (manifest: any) => {
      const slices = manifest.timeSlices ?? []
      // A transient empty manifest shouldn't erase this region's last-good data.
      if (slices.length === 0) {
        return
      }

      const newEpochs = slices.map((slice: any) => slice.forecastEpoch)
      const existing = regionTimesRef.current[manifest.region] ?? []
      regionTimesRef.current[manifest.region] = Array.from(
        new Set([...existing, ...newEpochs])
      )

      recomputeAvailableTimes(false)
    }

    // Clean up any existing layers
    layers.forEach((layer) => {
      layer.enable(false)
    })
    const feetToMeters = 1 / 3.28084
    const cloudColorMap = Terrier.createColorMap(
      [
        0.0 * feetToMeters,
        500.0 * feetToMeters,
        500.0 * feetToMeters,
        900.0 * feetToMeters,
        900.0 * feetToMeters,
        1000.0 * feetToMeters,
        1000.0 * feetToMeters,
        3000.0 * feetToMeters,
        3000.0 * feetToMeters,
        4000.0 * feetToMeters,
        4000.0 * feetToMeters,
        5000.0 * feetToMeters,
        5000.0 * feetToMeters,
      ],
      [
        0xff800000, 0xff800000, 0xffff0000, 0xffff0000, 0xffffff00, 0xffffff00,
        0xffff6600, 0xffff6600, 0xff000080, 0xff000080, 0xff003300, 0xff003300,
        0xff0000,
      ]
    )

    const statMileToMeters = 1609.34
    const visColorMap = Terrier.createColorMap(
      [
        0,
        1 * statMileToMeters,
        1 * statMileToMeters,
        3 * statMileToMeters,
        3 * statMileToMeters,
        5 * statMileToMeters,
        5 * statMileToMeters,
        7 * statMileToMeters,
        7 * statMileToMeters,
        10 * statMileToMeters,
        10 * statMileToMeters,
      ],
      [
        0xff800000, 0xff800000, 0xffda0000, 0xffda0000, 0xffffff00, 0xffffff00,
        0xff00ff00, 0xff00ff00, 0xff003300, 0xff003300, 0xff0000,
      ]
    )

    // when passing the values pass dont convert to truweather units
    const windColorMap = Terrier.createColorMap(
      [
        0, 2.57, 2.57, 5.14, 5.14, 7.72, 7.72, 10.29, 10.29, 12.86, 12.86,
        15.43, 15.43, 18.01, 18.01, 20.58, 20.58, 23.15, 23.15, 25.72, 25.72,
        28.29, 28.29, 30.87, 30.87, 33.44, 33.44, 36.01, 36.01, 38.58, 38.58,
      ],
      [
        0xff0000, 0xff0000, 0xfff8d4f9, 0xfff8d4f9, 0xfff1a5f4, 0xfff1a5f4,
        0xffe074f1, 0xffe074f1, 0xff0045ff, 0xff0045ff, 0xff0099ff, 0xff0099ff,
        0xff00ceff, 0xff00ceff, 0xff00e8ff, 0xff00e8ff, 0xff07ffe6, 0xff07ffe6,
        0xff66d400, 0xff66d400, 0xff80fa04, 0xff80fa04, 0xffb4ff36, 0xffb4ff36,
        0xffeaff13, 0xffeaff13, 0xffffe501, 0xffffe501, 0xffffc808, 0xffffc808,
        0xfffe8708,
      ]
    )
    // Set up the layers we know about and enable the first one
    if (terrierOvl === undefined) {
      return
    }

    // Clean up any existing layers
    // Note: Should make this more intelligent
    layers.forEach((layer) => {
      layer.enable(false)
    })
    var newLayers = []
    // We can have a simple list of variables or absolutely everything
    var variables = Terrier.variablesForStack()
    const toInclude = [
      'reflectivity',
      'visual radar xweather',
      'visual radar myradar',
      'cloud_ceiling',
      'visibility',
      'temperature',
      'wind_uv',
      'wind_uv_80m',
      'wind_speed_gust',
    ]
    var newVariables = {}
    toInclude.forEach((variable) => {
      if (variable !== 'wind_uv_80m' && variable in variables) {
        newVariables[variable] = variables[variable]
      } else if (variable === 'wind_uv_80m') {
        newVariables[variable] = variables['wind_uv']
        newVariables[variable].level = ['80m']
      }
    })
    variables = newVariables
    for (let varName in variables) {
      let variable = variables[varName]
      var newLayer = null
      // We can filter by region or source, optionally
      var searchParams = { variable: variable.name }
      if (variable.dataType != 'visual') {
        if (varName == 'reflectivity') {
          // Note: We can set radarOnly here to just see radar data
          searchParams['source'] = ['mrms']
          //changing to composite-refectivity from base
          searchParams['product'] = ['mcr']
        }
        if (varName === 'cloud_ceiling') {
          searchParams['source'] = [
            //'rtma',
            'meandair',
            //'glmp',
            'cuwx',
          ]
        } 
        //Adding cuwx-10m and 80m to area weather stack.
        else if (varName === 'wind_uv' || varName === 'wind_uv_80m') {
          searchParams['source'] = ['gfs', 'rtma', 'hrrr', 'mmatic','cuwx']
        }
        else if (varName === 'visibility'){
          searchParams['source'] = [
            'gfs',
            'rtma',
            'hrrr',
            'mmatic',
            'meandair',
            'glmp',
            'cuwx',
          ]
        }
        else {
          // Filtering down to these three sources will satisfy most people
          searchParams['source'] =  ['gfs', 'rtma', 'mrms', 'meandair','hrrr','mmatic','cuwx']
        }
      }
      if (varName.includes('visual radar')) {
        searchParams['variable'] = 'radar'
        searchParams['source'] = variable.source.name
      }
      if (varName === 'wind_uv') {
        searchParams['level'] = '10m'
      }
      if (varName.includes('wind_uv_80m')) {
        searchParams['level'] = '80m'
      }
      let sources = Terrier.sourcesForVariable(searchParams)
      // This can happen if we're filtering other things
      if (sources.length == 0) {
        continue
      }
      let colorMap =
        varName === 'cloud_ceiling'
          ? cloudColorMap
          : varName === 'wind_uv' ||
            varName === 'wind_uv_80m' ||
            varName === 'wind_speed_gust'
          ? windColorMap
          : varName === 'visibility'
          ? visColorMap
          : Terrier.colorMapForVariable(sources[0])
      let timeRange = timeRangeForVariable(sources[0])
      let levels = Terrier.variableLevelsForStack(sources[0].name)
      let interp = interpForVariable(sources[0])
      let units = unitsForVariable(sources[0])

      switch (variable.dataType) {
        case 'wind_uv':
          console.log("Units")
          console.log(units)
          newLayer = new Layer(ovl, {
            displayName: variable.name,
            layerName: variable.name,
            sources: sources,
            levels: levels,
            units: units,
            colorsGrey: colorMap,
            colors: colorMap,
            arrows: {
              cutoff: 1.0,
              speed: [2.57, 40.0],
              size: [
                [10, 20],
                [40, 80],
              ],
              layout: [80, 80],
              colors: [0xaa000000, 0xff000000],
            },
            timeRange: timeRange,
            loadCallback: onManifestLoad,
            interpMode: interp,
          })
          break
        case 'reflectivity':
          timeRange = [-1 * 60 * 60, 0.0, 64]
          newLayer = new Layer(ovl, {
            displayName: variable.name,
            layerName: variable.name,
            sources: sources,
            levels: levels,
            units: 'dBz',
            colorsGrey: colorMap,
            colors: colorMap,
            importanceScale: 16.0,
            timeRange: timeRange,
            loadCallback: onManifestLoad,
          })
          break
        case 'visual':
          // We can use defaults in most cases to display these
          console.log("visual")
          console.log(variable.name)
          newLayer = new Layer(ovl, {
            displayName: variable.name,
            layerName: variable.name,
            sources: sources,
            levels: levels,
            units: units,
            colorsGrey: colorMap,
            colors: colorMap,
            importanceScale: 24,
            timeRange: timeRange,
            loadCallback: onManifestLoad,
            interpMode: interp,
          })
          break
        default:
          // We can use defaults in most cases to display these
          console.log("default")
          console.log(variable.name)
          newLayer = new Layer(ovl, {
            displayName: variable.name,
            layerName: variable.name,
            sources: sources,
            levels: levels,
            units: units,
            colorsGrey: colorMap,
            colors: colorMap,
            timeRange: timeRange,
            loadCallback: onManifestLoad,
            interpMode: interp,
          })
          break
      }
      newLayer.getDisplayName() === 'wind_speed_gust' ||
      newLayer.getDisplayName() === 'wind_uv'
        ? newLayer.opacityUpdate(140)
        : newLayer.getDisplayName() === 'radar'
        ? newLayer.opacityUpdate(200)
        : newLayer.opacityUpdate(110)
      if (newLayer) {
        newLayers.push(newLayer)
      }
    }
    const casaRadarSources = Terrier.sourcesForVariable({
      variable: 'reflectivity',
      region: 'casa_denton_tx',
    })
    //first two digits are the alpha channel : they define opaqueness for the color.
    //the scheme is : 0xaarrggbb where a is alpha, r is red, g is green and b is blue.
    const casaRadarColorMap = Terrier.createColorMap(
      [-10, -5, 0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75],
      [
        0x0010e6e7, 0xff10e6e7, 0xff069ff3, 0xff0400f0, 0xff01fc08, 0xff02c701,
        0xff068d01, 0xfff6f602, 0xffe6ba03, 0xfff79505, 0xfffe0002, 0xffd60401,
        0xffbb0200, 0xfff807f6, 0xff9a52c8, 0xff6e49a2, 0xff5a32a2, 0xfffcfbfa,
      ]
    )
    const casaRadarLayer = new Layer(ovl, {
      displayName: 'reflectivity',
      layerName: 'casa',
      sources: casaRadarSources,
      levels: Terrier.variableLevelsForStack('reflectivity'),
      units: 'dBz',
      colorsGrey: casaRadarColorMap,
      colors: casaRadarColorMap,
      importanceScale: 16.0,
      timeRange: [-1 * 60 * 60, 0.0, 64],
      loadCallback: onManifestLoad,
    })
    newLayers.push(casaRadarLayer)
    setLayers([...newLayers])
    if (newLayers.length > 0) {
      setCurLayer({
        layer: 0,
      })
      _setUnits(newLayers[0].units)
    }

    dispatch(
      setCurLayer({
        layer: -1,
      })
    )
    _setUnits(newLayers[0].units)
    // ovl.setNearestFrame(true)
    const now = Date.now() / 1000
    setCurTime(now)
  }
  // startUpFunc()
  useEffect(() => {
    if (!isPending && !isError && customUnits) {
      startUpFunc()
    }
    map.on('click', onMapClickHandler);

    return () => {
      map.off('click', onMapClickHandler);
    }
  }, [map, customUnits, isPending, isError])

  const canDisplayLegend =
    legendVisible && curLayer.layer >= 0 && curLayer.layer < layers.length

  const isRadarLayer =
    curLayer.layer === WetDogWeatherLayersEnum.RADAR ||
    curLayer.layer === WetDogWeatherLayersEnum.XWEATHER_RADAR

  const canDisplayMediaControls =
    curTime != Number.NEGATIVE_INFINITY &&
    layers.length > 0 &&
    curLayer.layer >= 0 &&
    curLayer.layer < layers.length

  const canDisplayCanvas = curLayer.layer >= 0 && curLayer.layer < layers.length

  const onMapClickHandler = (e: LeafletMouseEvent) => {
    const layers = Terrier.ovl.getLayers()
    if (layers.length > 0) {
      const layer = layers[0]
      if (layer.name === 'visual') {
        return
      } else {
        const x = e.containerPoint.x
        const y = e.containerPoint.y
        const ret = layer.queryValue(x, y)
        if (ret === null || !ret['value']) {
          return
        } else if (layer.name === 'windUV') {
          ret['value'] = [
            Math.sqrt(
              Math.pow(ret['value'][0], 2) + Math.pow(ret['value'][1], 2)
            ),
          ]
        }
        const unit = getUnitForLayer(layer.name, customUnits)
        const sourceUnit = getSourceUnitForLayer(layer.name)
        let value = convertValuesToTruweatherUnits(ret['value'][0], unit, sourceUnit)
        value = formatValue(value, layer.name)
        new L.Popup()
          .setContent(
            `<div className=''>Location : ${
              e.latlng.lat.toFixed(5) + ', ' + e.latlng.lng.toFixed(5)
            }</div>
            <div>Value : ${
              value.toString() + ' ' + unit
            }</div>`
          )
          .setLatLng(e.latlng)
          .openOn(map)
      }
    }
  }

function embindToPlain(o, seen = new WeakSet()) {
  if (o == null || typeof o !== "object") return o;
  if (seen.has(o)) return "[Circular]";
  seen.add(o);
  if (Array.isArray(o)) return o.map(x => embindToPlain(x, seen));

  const out = {};

  // 1. own enumerable properties (covers plain dictionaries)
  for (const key of Object.keys(o)) {
    try { out[key] = embindToPlain(o[key], seen); }
    catch (e) { out[key] = `<error: ${e.message}>`; }
  }

  // 2. getters walking up the prototype chain (covers Embind ClassHandle)
  let proto = Object.getPrototypeOf(o);
  while (proto && proto !== Object.prototype) {
    for (const key of Object.getOwnPropertyNames(proto)) {
      if (key in out) continue;
      if (Object.getOwnPropertyDescriptor(proto, key)?.get) {
        try { out[key] = embindToPlain(o[key], seen); }
        catch (e) { out[key] = `<error: ${e.message}>`; }
      }
    }
    proto = Object.getPrototypeOf(proto);
  }

  return out;
}

  function getUnitForLayer(
    layerName: string,
    customUnits?: Record<string, string>
  ): string {
    // wetdog weather has two wind layers windUV for surface winds and 80m winds
    // wind_speed_gust for Wind gust
    // we need to map it to wind_speed
    if (layerName === 'wind_uv' || layerName === 'wind_speed_gust' || layerName === 'windUV') {
      layerName = 'wind_speed';
    }
    if (layerName === 'cloud_ceiling') {
      layerName = 'ceiling';
    }
    // If custom units exist and have a mapping for this layer, use it
    if (customUnits && customUnits[layerName]) {
      return customUnits[layerName]
    }
    // Otherwise fall back to the default enum
    return LayerToUnitsMapping[layerName as keyof typeof LayerToUnitsMapping] ?? ''
  }

  // The unit raw values are actually queried from the map in, per layer -
  // Kelvin for temperature, meters for visibility/ceiling, m/s for wind speed.
  function getSourceUnitForLayer(layerName: string): string | undefined {
    switch (layerName) {
      case 'temperature':
        return 'K'
      case 'wind_uv':
      case 'wind_speed_gust':
      case 'windUV':
        return 'm/s'
      case 'visibility':
      case 'cloud_ceiling':
        return 'm'
      default:
        return undefined
    }
  }

  return (
    <div className={'w-full h-full relative'}>
      {canDisplayMediaControls && (
        <div
          className={
            'w-2/3 absolute left-1/2 right-1/2 -translate-x-1/2 bottom-10 h-20 z-[99999998]'
          }
        >
          {canDisplayMediaControls && (
            <MediaControls
              curTime={displayedTime}
              setCurTime={setCurTime}
              isPlaying={isPlaying}
              timeRange={timeRange}
              setIsPlaying={setIsPlaying}
              availableTimes={availableTimes}
            />
          )}
        </div>
      )}
      {canDisplayLegend && isRadarLayer && (
        <img
          className="w-56 h-10 flex flex-col text-center items-center justify-center p-2 z-[9999]
        absolute right-0 top-0"
          src={require('../../assets/my_radar_legend.png')}
          alt={'my radar legend'}
        />
      )}
      <div className="fixed top-16 right-16 z-[999999] flex flex-col items-end gap-4">
        {canDisplayLegend && !isRadarLayer && (
          <Legend
            colorMap={layers[curLayer.layer].getColorMap()}
            units={getUnitForLayer(layers[curLayer.layer].getDisplayName(), customUnits)}
          />
        )}
        {/* {canDisplayCanvas && (
          <OpacityControl curLayer={curLayer} layers={layers} />
        )} */}
      </div>
      {canDisplayCanvas && <canvas ref={canvasRef}></canvas>}
    </div>
  )
}

export default WetDogWeatherLayers
